import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";

interface PipelineConfigSummary {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export function EmailAutomationPipelineListPage() {
  const [configs, setConfigs] = useState<PipelineConfigSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] =
    useState<PipelineConfigSummary | null>(null);

  // Form state
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [useSampleData, setUseSampleData] = useState(true);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ configs: PipelineConfigSummary[] }>(
        `/admin/email-automation/config/list`,
        {}
      );
      setConfigs(res.configs);
    } catch (error) {
      console.error("Error fetching configs:", error);
      setError("Failed to load pipeline configurations");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConfig = async () => {
    try {
      setError(null);
      const res = await apiClient.post<{ config: PipelineConfigSummary }>(
        `/admin/email-automation/config/create`,
        {
          name: newName.trim(),
          description: newDescription.trim() || undefined,
          useSampleData,
        }
      );
      setConfigs([res.config, ...configs]);
      setCreateDialogOpen(false);
      setNewName("");
      setNewDescription("");
      setUseSampleData(true);
      showSnackbar("Configuration created successfully", 3000, "success");
      // Navigate to edit page
      navigate(`/admin/email-automation/config/${res.config.id}`);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to create configuration";
      setError(errorMessage);
    }
  };

  const handleDeleteConfig = async () => {
    if (!selectedConfig) return;

    try {
      setError(null);
      await apiClient.delete(`/admin/email-automation/config/delete`, {
        data: { id: selectedConfig.id },
      });
      setConfigs(configs.filter((c) => c.id !== selectedConfig.id));
      setDeleteConfirmOpen(false);
      setSelectedConfig(null);
      showSnackbar("Configuration deleted successfully", 3000, "success");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to delete configuration";
      setError(errorMessage);
    }
  };

  const handleDuplicate = async (config: PipelineConfigSummary) => {
    try {
      // First fetch the full config
      const fullConfig = await apiClient.post<{ config: any }>(
        `/admin/email-automation/config/get`,
        { id: config.id }
      );

      // Create a copy with a new name
      const res = await apiClient.post<{ config: PipelineConfigSummary }>(
        `/admin/email-automation/config/create`,
        {
          name: `${config.name} (Copy)`,
          description: config.description || undefined,
          categorizationStepConfig: fullConfig.config.categorizationStepConfig,
          propertyExtractionStepConfig:
            fullConfig.config.propertyExtractionStepConfig,
          apiFetchStepConfig: fullConfig.config.apiFetchStepConfig,
          presentationStepConfig: fullConfig.config.presentationStepConfig,
        }
      );
      setConfigs([res.config, ...configs]);
      showSnackbar("Configuration duplicated successfully", 3000, "success");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to duplicate configuration";
      showSnackbar(errorMessage, 4000, "error");
    }
  };

  if (loading) {
    return (
      <AdminPage title="Email Automation Pipeline">
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage title="Email Automation Pipeline">
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">Pipeline Configurations</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate("/admin/email-test-sets")}
            >
              Manage Test Sets
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Configuration
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No configurations yet. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((config) => (
                  <TableRow
                    key={config.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/admin/email-automation/config/${config.id}`)
                    }
                  >
                    <TableCell>
                      <Typography fontWeight="medium">{config.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color="text.secondary"
                        sx={{
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {config.description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(config.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(config.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Duplicate">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(config);
                          }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/admin/email-automation/config/${config.id}`
                            );
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedConfig(config);
                            setDeleteConfirmOpen(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Pipeline Configuration</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              sx={{ mt: 1 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Description (optional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              sx={{ mt: 2 }}
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={useSampleData}
                  onChange={(e) => setUseSampleData(e.target.checked)}
                />
              }
              label="Pre-fill with sample configuration"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateConfig}
              disabled={!newName.trim()}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Delete Configuration</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{selectedConfig?.name}"? This
              action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfig}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
